/**
 * GamificationDashboard - Points, Badges, Streaks & Rewards
 * USER-CON-001 Phase 5
 *
 * Features:
 * - Points summary and tier progress
 * - Badge showcase and progress
 * - Streak tracking with visual calendar
 * - Flash rewards
 * - Point history
 */
import React, { useState, useEffect } from 'react';
import {
  Award, Star, Flame, TrendingUp, Gift, History,
  ArrowLeft, ChevronRight, Lock, Zap, Trophy,
  Calendar, Target, Crown
} from 'lucide-react';
import {
  getPointsSummary,
  getPointHistory,
  getUserBadges,
  getBadgeProgress,
  getStreakInfo,
  getTierInfo,
  getFlashRewards,
  claimFlashReward,
  recordDailyActivity,
} from '../../services/gamificationApi';

// Tier configuration
const TIERS = {
  collector: { name: 'Collector', color: 'text-zinc-400', icon: Star, threshold: 0 },
  grader: { name: 'Grader', color: 'text-green-400', icon: Award, threshold: 500 },
  presser: { name: 'Presser', color: 'text-blue-400', icon: Zap, threshold: 2000 },
  slabber: { name: 'Slabber', color: 'text-purple-400', icon: Trophy, threshold: 5000 },
  slab_master: { name: 'Slab Master', color: 'text-orange-400', icon: Crown, threshold: 10000 },
};

// Points Summary Card
function PointsSummaryCard({ points, tier, nextTier, pointsToNext, progress }) {
  const TierIcon = TIERS[tier]?.icon || Star;
  const tierConfig = TIERS[tier] || TIERS.collector;

  return (
    <div className="bg-gradient-to-br from-orange-500/20 to-purple-500/20 border border-orange-500/30 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-zinc-400 text-sm">Total Points</p>
          <p className="text-4xl font-bold text-white">{(points || 0).toLocaleString()}</p>
        </div>
        <div className={`p-3 rounded-full ${tierConfig.color.replace('text-', 'bg-').replace('-400', '-500/20')}`}>
          <TierIcon className={`w-8 h-8 ${tierConfig.color}`} />
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <span className={`text-lg font-medium ${tierConfig.color}`}>
          {tierConfig.name}
        </span>
        {nextTier && (
          <>
            <ChevronRight className="w-4 h-4 text-zinc-500" />
            <span className="text-zinc-400">{TIERS[nextTier]?.name}</span>
          </>
        )}
      </div>

      {nextTier && (
        <>
          <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-orange-500 to-purple-500 transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-sm text-zinc-400 mt-2">
            {pointsToNext?.toLocaleString()} points to {TIERS[nextTier]?.name}
          </p>
        </>
      )}
    </div>
  );
}

// Streak Card
function StreakCard({ streak, longestStreak, onCheckin }) {
  const [hasCheckedIn, setHasCheckedIn] = useState(false);

  const handleCheckin = async () => {
    try {
      await recordDailyActivity();
      setHasCheckedIn(true);
      onCheckin?.();
    } catch (error) {
      console.error('Check-in failed:', error);
    }
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-white flex items-center gap-2">
          <Flame className="w-5 h-5 text-orange-500" />
          Daily Streak
        </h3>
        {!hasCheckedIn && (
          <button
            onClick={handleCheckin}
            className="px-3 py-1 bg-orange-500 hover:bg-orange-600 text-white text-sm rounded-full transition-colors"
          >
            Check In
          </button>
        )}
      </div>

      <div className="flex items-end gap-6">
        <div>
          <p className="text-5xl font-bold text-orange-400">{streak || 0}</p>
          <p className="text-sm text-zinc-400">Current</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-zinc-300">{longestStreak || 0}</p>
          <p className="text-sm text-zinc-500">Best</p>
        </div>
      </div>

      {/* Milestone indicators */}
      <div className="flex gap-2 mt-4">
        {[7, 30, 100, 365].map((milestone) => (
          <div
            key={milestone}
            className={`flex-1 text-center py-2 rounded ${
              (streak || 0) >= milestone
                ? 'bg-orange-500/20 text-orange-400'
                : 'bg-zinc-800 text-zinc-500'
            }`}
          >
            <p className="text-xs">{milestone}d</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// Badge Card
function BadgeCard({ badge, isEarned }) {
  return (
    <div
      className={`relative p-4 rounded-xl transition-all ${
        isEarned
          ? 'bg-zinc-900 border border-zinc-700'
          : 'bg-zinc-900/50 border border-zinc-800/50 opacity-60'
      }`}
    >
      {!isEarned && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Lock className="w-6 h-6 text-zinc-600" />
        </div>
      )}

      <div className="flex items-center gap-3">
        <div className={`text-3xl ${!isEarned && 'opacity-30'}`}>
          {badge.icon_emoji || '🏆'}
        </div>
        <div className={!isEarned ? 'opacity-30' : ''}>
          <p className="font-medium text-white text-sm">{badge.name}</p>
          <p className="text-xs text-zinc-400">{badge.description}</p>
        </div>
      </div>

      {isEarned && badge.awarded_at && (
        <p className="text-xs text-zinc-500 mt-2">
          Earned {new Date(badge.awarded_at).toLocaleDateString()}
        </p>
      )}

      {!isEarned && badge.progress !== undefined && (
        <div className="mt-3">
          <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-zinc-600"
              style={{ width: `${badge.progress}%` }}
            />
          </div>
          <p className="text-xs text-zinc-500 mt-1">{badge.progress}%</p>
        </div>
      )}
    </div>
  );
}

// Flash Reward Card
function FlashRewardCard({ reward, onClaim }) {
  const expiresAt = new Date(reward.expires_at);
  const timeLeft = expiresAt - new Date();
  const hoursLeft = Math.max(0, Math.floor(timeLeft / (1000 * 60 * 60)));

  return (
    <div className="bg-gradient-to-r from-purple-500/20 to-orange-500/20 border border-purple-500/30 rounded-xl p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-500/20 rounded-lg">
            <Gift className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <p className="font-medium text-white">{reward.title}</p>
            <p className="text-sm text-zinc-400">{reward.description}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm text-orange-400">{hoursLeft}h left</p>
          <button
            onClick={() => onClaim(reward.id)}
            className="mt-1 px-3 py-1 bg-purple-500 hover:bg-purple-600 text-white text-sm rounded-full transition-colors"
          >
            Claim
          </button>
        </div>
      </div>
    </div>
  );
}

// Point History Item
function PointHistoryItem({ transaction }) {
  const isPositive = transaction.points > 0;

  return (
    <div className="flex items-center justify-between py-3 border-b border-zinc-800 last:border-0">
      <div>
        <p className="text-white text-sm">{transaction.description}</p>
        <p className="text-xs text-zinc-500">
          {new Date(transaction.created_at).toLocaleDateString()}
        </p>
      </div>
      <span className={`font-medium ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
        {isPositive ? '+' : ''}{transaction.points}
      </span>
    </div>
  );
}

// Main Dashboard Component
export default function GamificationDashboard({ onBack }) {
  const [points, setPoints] = useState({});
  const [badges, setBadges] = useState([]);
  const [badgeProgress, setBadgeProgress] = useState([]);
  const [streak, setStreak] = useState({});
  const [flashRewards, setFlashRewards] = useState([]);
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [pointsData, badgesData, progressData, streakData, rewardsData, historyData] = await Promise.all([
        getPointsSummary().catch(() => ({})),
        getUserBadges().catch(() => []),
        getBadgeProgress().catch(() => []),
        getStreakInfo().catch(() => ({})),
        getFlashRewards().catch(() => []),
        getPointHistory({ per_page: 10 }).catch(() => ({ transactions: [] })),
      ]);

      setPoints(pointsData);
      setBadges(badgesData);
      setBadgeProgress(progressData);
      setStreak(streakData);
      setFlashRewards(rewardsData);
      setHistory(historyData.transactions || []);
    } catch (error) {
      console.error('Failed to load gamification data:', error);
    }
    setIsLoading(false);
  };

  const handleClaimReward = async (rewardId) => {
    // This was "// API call would go here" plus a local filter, so claiming removed the reward
    // from the screen and recorded NOTHING. The reward was still unclaimed server-side, and the
    // user had no way to tell -- it looked exactly like a successful claim. The endpoint exists
    // (POST /gamification/rewards/claim); only the call was missing.
    try {
      await claimFlashReward(rewardId);
      setFlashRewards((prev) => prev.filter((r) => r.id !== rewardId));
    } catch (error) {
      // Leave the reward on screen. A claim that did not happen must not look like one.
      console.error('Claim failed:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back</span>
        </button>

        <h1 className="font-comic text-3xl sm:text-4xl text-white flex items-center gap-3 mb-2">
          <Trophy className="w-8 h-8 text-orange-500" />
          Rewards <span className="text-orange-500">Center</span>
        </h1>
        <p className="text-zinc-400">Track your progress and earn rewards</p>

        {/* Tabs */}
        <div className="flex gap-4 mt-6 border-b border-zinc-800">
          {['overview', 'badges', 'history'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-3 px-1 text-sm font-medium capitalize transition-colors ${
                activeTab === tab
                  ? 'text-orange-400 border-b-2 border-orange-500'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 pb-16">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            {/* Points Summary */}
            <PointsSummaryCard
              points={points.total_points}
              tier={points.tier}
              nextTier={points.next_tier}
              pointsToNext={points.points_to_next_tier}
              progress={points.tier_progress_pct}
            />

            {/* Streak */}
            <StreakCard
              streak={streak.current_streak}
              longestStreak={streak.longest_streak}
              onCheckin={loadData}
            />

            {/* Flash Rewards */}
            {flashRewards.length > 0 && (
              <div className="lg:col-span-2">
                <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-400" />
                  Flash Rewards
                </h3>
                <div className="space-y-3">
                  {flashRewards.map((reward) => (
                    <FlashRewardCard
                      key={reward.id}
                      reward={reward}
                      onClaim={handleClaimReward}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Recent Badges */}
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-white flex items-center gap-2">
                  <Award className="w-5 h-5 text-purple-400" />
                  Recent Badges
                </h3>
                <button
                  onClick={() => setActiveTab('badges')}
                  className="text-sm text-orange-400 hover:text-orange-300"
                >
                  View All
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {badges.slice(0, 4).map((badge) => (
                  <BadgeCard key={badge.id} badge={badge} isEarned />
                ))}
                {badges.length === 0 && (
                  <div className="col-span-full text-center py-8 text-zinc-500">
                    <Award className="w-12 h-12 text-zinc-700 mx-auto mb-2" />
                    <p>No badges earned yet</p>
                    <p className="text-sm">Keep collecting to unlock badges!</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'badges' && (
          <div className="mt-6">
            {/* Earned Badges */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-white mb-4">
                Earned ({badges.length})
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {badges.map((badge) => (
                  <BadgeCard key={badge.id} badge={badge} isEarned />
                ))}
              </div>
            </div>

            {/* In Progress */}
            {badgeProgress.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-white mb-4">
                  In Progress ({badgeProgress.length})
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {badgeProgress.map((badge) => (
                    <BadgeCard key={badge.badge_id} badge={badge} isEarned={false} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="mt-6">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
              <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                <History className="w-5 h-5 text-zinc-400" />
                Point History
              </h3>
              {history.length > 0 ? (
                <div>
                  {history.map((tx) => (
                    <PointHistoryItem key={tx.id} transaction={tx} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-zinc-500">
                  <History className="w-12 h-12 text-zinc-700 mx-auto mb-2" />
                  <p>No point history yet</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
